// Helpers for the "Flexible dates" calendar grid.
// The backend keys its calendar recommendations dictionary by a DDMMYY date
// code (derived from the Amadeus response), built here the same way the
// existing Angular app does it in FlightCalendarComponent.reformatDate().

const pad2 = (n) => String(n).padStart(2, "0");

export const formatDateISO = (date) => {
    return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;
};

// "2026-07-24" -> "240726"
const toDateCode = (isoDate) => {
    return isoDate.substring(2).split("-").reverse().join("");
};

export const reformatDateKey = (outboundISO, inboundISO) => {
    const outb = toDateCode(outboundISO);
    if (inboundISO) {
        const inb = toDateCode(inboundISO);
        return `${outb}-${inb}`;
    }
    return outb;
};

// List of ISO dates from centerISO-range to centerISO+range (inclusive).
export const getDateRange = (centerISO, range) => {
    if (!centerISO) return [];
    const center = new Date(centerISO);
    const dates = [];
    for (let i = -range; i <= range; i++) {
        const d = new Date(center);
        d.setDate(d.getDate() + i);
        dates.push(formatDateISO(d));
    }
    return dates;
};

export const formatDayLabel = (isoDate, locale = "fr-FR") => {
    const date = new Date(isoDate);
    return date.toLocaleDateString(locale, { day: "2-digit", month: "short" });
};

// Traffic-light price scale: green = best (cheapest), yellow = mid-range,
// red = worst (most expensive). Single source of truth - both the cell
// fills and the legend gradient read from these three stops.
export const BEST_PRICE_HEX = "#31b1206c"; // green - cheapest
export const MID_PRICE_HEX = "#f5d41aec"; // yellow - mid-range
export const WORST_PRICE_HEX = "#f72323"; // red - priciest
export const HEADER_BG = "#184f95";
// Blue reads clearly against green/yellow/red alike, and isn't one of the
// price-scale colors itself, so a selected cell is unambiguous.
export const SELECTION_ACCENT = "#2a78d6";

const hexToRgb = (hex) => {
    const h = hex.replace("#", "");
    return [parseInt(h.substring(0, 2), 16), parseInt(h.substring(2, 4), 16), parseInt(h.substring(4, 6), 16)];
};

const toHex = (r, g, b) =>
    `#${[r, g, b].map((c) => Math.round(c).toString(16).padStart(2, "0")).join("")}`;

export const makeColor = (price, min, max) => {
    if (min === max) return MID_PRICE_HEX;

    let ratio = price;
    if (min > 0 || max < 1) {
        if (price < min) ratio = 0;
        else if (price > max) ratio = 1;
        else ratio = (price - min) / (max - min);
    }

    const [c1, c2, t] =
        ratio <= 0.5
            ? [hexToRgb(BEST_PRICE_HEX), hexToRgb(MID_PRICE_HEX), ratio / 0.5]
            : [hexToRgb(MID_PRICE_HEX), hexToRgb(WORST_PRICE_HEX), (ratio - 0.5) / 0.5];

    return toHex(c1[0] + (c2[0] - c1[0]) * t, c1[1] + (c2[1] - c1[1]) * t, c1[2] + (c2[2] - c1[2]) * t);
};

// Picks readable ink for a label placed inside a colored fill, by the
// fill's luminance (marks-and-anatomy.md: "pick white or ink by the fill's
// luminance so it always clears contrast").
export const getContrastText = (bgHex) => {
    const hex = bgHex.replace("#", "");
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return luminance > 0.6 ? "#0b0b0b" : "#ffffff";
};

// Filters, ported from FlightCalendarComponent (Angular): airline / stops /
// certificate filters don't remove a date from the grid - they re-rank each
// date's recommendation list so the cheapest MATCHING one sits at index 0
// (or null if nothing matches), same as Angular's filter()/applyFilters().
export const EMPTY_CALENDAR_FILTERS = {
    airlines: [],
    outboundStops: [],
    inboundStops: [],
    certificateOnly: false, // "Sans Attestation" - exclude flights that need exchange documents
};

const reorderByProperty = (arr, filterValues, property) => {
    const matching = arr.filter((item) => item && filterValues.includes(item[property]));
    const nonMatchCount = arr.length - matching.length;
    return [...matching, ...Array(nonMatchCount).fill(null)];
};

export const applyCalendarFilters = (arr, filters) => {
    let result = arr;
    if (filters.airlines.length) result = reorderByProperty(result, filters.airlines, "validatingCarrier");
    if (filters.outboundStops.length) result = reorderByProperty(result, filters.outboundStops, "bound_1");
    if (filters.inboundStops.length) result = reorderByProperty(result, filters.inboundStops, "bound_2");
    if (filters.certificateOnly) {
        result = result.map((item) => (item && item.flightNeedExchangeDocuments ? null : item));
    }
    return result;
};
